// SignUpModal.tsx
import React, { useMemo, useState } from "react";
import Modal from "react-modal";
import axios, { AxiosError } from "axios";
import { GetBaseURL } from "./utils/urlUtils";
import { errorMessage as apiErrorMessage } from "./ui/Toast";
import {
  X,
  Building2,
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  Loader2,
  AlertCircle,
  ArrowLeft,
  MapPin,
  Send,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../assets/KavoaLogo.png";

Modal.setAppElement("#root");

interface SignUpModalProps {
  onSuccessfulLogin: (data: {
    token: string;
    userId: string;
    firmId?: string;
  }) => void;

  // NEW (optional): controlled modal open
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const SignUpModal: React.FC<SignUpModalProps> = ({
  onSuccessfulLogin,
  isOpen,
  onOpenChange,
}) => {
  const isControlled = typeof isOpen === "boolean" && !!onOpenChange;

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? isOpen : internalOpen;

  const setOpen = (next: boolean) => {
    if (isControlled) onOpenChange!(next);
    else setInternalOpen(next);
  };

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firmName, setFirmName] = useState("");
  const [name, setName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorType, setErrorType] = useState<"firm_exists" | "email_exists" | "invited_user" | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Location step state
  const [country, setCountry] = useState("");
  const [step, setStep] = useState<"location" | "details">("location");

  const countries = [
    {
      code: "CA",
      name: "Canada",
      icon: "fab fa-canadian-maple-leaf",
      iconColor: "#d52b1e",
      gradient: "linear-gradient(135deg, #fee2e2, #fecaca)",
    },
    {
      code: "US",
      name: "United States",
      icon: "fas fa-flag-usa",
      iconColor: "#1e40af",
      gradient: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
    },
    {
      code: "GB",
      name: "United Kingdom",
      icon: "fas fa-crown",
      iconColor: "#7c3aed",
      gradient: "linear-gradient(135deg, #ede9fe, #ddd6fe)",
    },
    {
      code: "AU",
      name: "Australia",
      icon: "fas fa-earth-oceania",
      iconColor: "#d97706",
      gradient: "linear-gradient(135deg, #fef3c7, #fde68a)",
    },
  ];

  const selectedCountryName = countries.find((c) => c.code === country)?.name || "";

  // Request Access state
  const [showAccessRequest, setShowAccessRequest] = useState(false);
  const [accessRequesting, setAccessRequesting] = useState(false);
  const [accessSuccess, setAccessSuccess] = useState(false);
  const [accessForm, setAccessForm] = useState({
    adminEmail: "",
    name: "",
    email: "",
  });

  // Waitlist form state
  const [waitlistForm, setWaitlistForm] = useState({ email: "", firmName: "", message: "" });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState("");

  const closeModal = () => {
    setOpen(false);
    setErrorMessage("");
    setErrorType(null);
    setStep("location");
    setCountry("");
    setEmail("");
    setPassword("");
    setFirmName("");
    setName("");
    setConsentAccepted(false);
    setShowPassword(false);
    setShowAccessRequest(false);
    setAccessSuccess(false);
    setAccessForm({ adminEmail: "", name: "", email: "" });
    setWaitlistForm({ email: "", firmName: "", message: "" });
    setWaitlistSubmitting(false);
    setWaitlistSuccess(false);
    setWaitlistError("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage("");
    setErrorType(null);
    setSubmitting(true);

    try {
      const response = await axios.post(`${GetBaseURL()}/api/auth/signup`, {
        email,
        password,
        name,
        firmName,
        consentAccepted,
        country,
      });

      if (response.data.token && response.data.user.id) {
        onSuccessfulLogin({
          token: response.data.token,
          userId: response.data.user.id,
          firmId: response.data.user.firmId,
        });
        localStorage.setItem("accessToken", response.data.token);
        closeModal();
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (!axiosError.response) {
        // Network error  -  server unreachable
        setErrorMessage("Could not connect to the server. Please check your internet connection and try again.");
        setErrorType(null);
      } else {
        const data = axiosError.response.data as any;
        const msg = data?.msg || "Something went wrong. Please try again.";
        setErrorMessage(msg);

        if (data?.code === "INVITED_USER") {
          setErrorType("invited_user");
        } else if (msg.toLowerCase().includes("firm with this name already exists")) {
          setErrorType("firm_exists");
        } else if (msg.toLowerCase().includes("email already exists")) {
          setErrorType("email_exists");
        } else {
          setErrorType(null);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccessRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setAccessRequesting(true);
    setErrorMessage("");

    try {
      await axios.post(`${GetBaseURL()}/api/auth/request-access`, {
        adminEmail: accessForm.adminEmail,
        name: accessForm.name,
        email: accessForm.email,
      });
      setAccessSuccess(true);
    } catch (error) {
      const axiosError = error as AxiosError;
      setErrorMessage(
        (axiosError.response?.data as any)?.msg || "An error occurred",
      );
    } finally {
      setAccessRequesting(false);
    }
  };

  const modalClassName = useMemo(
    () =>
      "w-[95vw] max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-100 outline-none overflow-hidden",
    [],
  );
  const overlayClassName = useMemo(
    () =>
      "fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]",
    [],
  );

  const benefits = [
    "Unlimited clients & matters",
    "Billing, documents & calendar",
    "Cancel anytime",
  ];

  const inputClassName =
    "w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

  return (
    <Modal
      isOpen={open}
      onRequestClose={closeModal}
      contentLabel="Register Firm Modal"
      className={modalClassName}
      overlayClassName={overlayClassName}
    >
      <div className="md:flex">
        {/* -- Left branded panel (hidden on mobile) -- */}
        <div
          className="hidden md:flex md:w-[45%] flex-col justify-between rounded-l-2xl p-8 text-white"
          style={{ backgroundColor: "#0F5132" }}
        >
          <div>
            <img
              src={Logo}
              alt="Kavoa"
              className="h-14 mb-10 brightness-0 invert"
            />

            <h2 className="text-2xl font-bold leading-tight mb-3">
              Start Managing Your Practice Smarter
            </h2>
            <p className="text-sm text-white/80 mb-8">
              14-day free trial. No credit card required.
            </p>

            <ul className="space-y-4">
              {benefits.map((text) => (
                <li key={text} className="flex items-center gap-3 text-sm">
                  <CheckCircle className="h-5 w-5 shrink-0 text-emerald-300" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-white/50 mt-8">
            Trusted by 500+ law firms nationwide
          </p>
        </div>

        {/* -- Right form panel -- */}
        <div className="relative flex-1 px-5 py-6 sm:p-8 overflow-y-auto max-h-[85vh]">
          {/* Close button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 z-10"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Mobile-only logo */}
          <div className="mb-6 md:hidden">
            <img src={Logo} alt="Kavoa" className="h-12" />
          </div>

          <AnimatePresence mode="wait">
            {showAccessRequest ? (
              /* ── Request Access Sub-Form ── */
              <motion.div
                key="access-request"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowAccessRequest(false);
                    setAccessSuccess(false);
                    setErrorMessage("");
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors mb-4"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to signup
                </button>

                <h2 className="text-2xl font-bold text-slate-800 mb-1">
                  Request Access
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  We'll notify your firm's administrator so they can add you
                </p>

                {/* Error message */}
                {errorMessage && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                {/* Success message */}
                {accessSuccess ? (
                  <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700">
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0" />
                    <span>
                      Your request has been sent to the firm administrator
                    </span>
                  </div>
                ) : (
                  <form onSubmit={handleAccessRequest} className="space-y-4">
                    {/* Administrator Email */}
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Administrator Email
                      </span>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                        <input
                          type="email"
                          placeholder="admin@yourfirm.com"
                          className={inputClassName}
                          value={accessForm.adminEmail}
                          onChange={(e) =>
                            setAccessForm((prev) => ({
                              ...prev,
                              adminEmail: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                    </label>

                    {/* Your Name */}
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Your Name
                      </span>
                      <div className="relative">
                        <User className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Jane Doe"
                          className={inputClassName}
                          value={accessForm.name}
                          onChange={(e) =>
                            setAccessForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                    </label>

                    {/* Your Email */}
                    <label className="block">
                      <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                        Your Email
                      </span>
                      <div className="relative">
                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                        <input
                          type="email"
                          placeholder="jane@lawfirm.com"
                          className={inputClassName}
                          value={accessForm.email}
                          onChange={(e) =>
                            setAccessForm((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                    </label>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={accessRequesting}
                      className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
                      style={{ backgroundColor: "#0F5132" }}
                    >
                      {accessRequesting ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Sending request...
                        </>
                      ) : (
                        "Request Access"
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            ) : step === "location" ? (
              /* ── Step 1: Location Selection ── */
              <motion.div
                key="location-step"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                <h2 className="text-2xl font-bold text-slate-800 mb-1">
                  Where is your firm located?
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Select your country to get started
                </p>

                {/* Country cards — 2x2 grid */}
                <div className="grid grid-cols-2 gap-3">
                  {countries.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => {
                        setCountry(c.code);
                        setWaitlistSuccess(false);
                        setWaitlistError("");
                        setWaitlistForm({ email: "", firmName: "", message: "" });
                      }}
                      className={`rounded-xl border-2 p-6 text-center cursor-pointer transition-all duration-200 ${
                        country === c.code
                          ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-500/20 scale-[1.02]"
                          : "border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 hover:scale-[1.01] hover:shadow-sm"
                      }`}
                    >
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-2.5 mx-auto shadow-sm"
                        style={{ background: c.gradient }}
                      >
                        <i className={c.icon} style={{ color: c.iconColor, fontSize: "20px" }} />
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{c.name}</span>
                    </button>
                  ))}
                </div>

                {/* Waitlist form for UK/AU */}
                <AnimatePresence>
                  {(country === "GB" || country === "AU") && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-6 bg-gradient-to-br from-emerald-50 to-slate-50 border border-emerald-200/60 rounded-xl p-6">
                        {/* Header */}
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="h-5 w-5 text-emerald-600" />
                          <h3 className="text-base font-bold text-emerald-900">
                            Kavoa is launching in {selectedCountryName} soon!
                          </h3>
                        </div>
                        <p className="text-sm text-emerald-700/80 mb-5 ml-7">
                          Join the waitlist to claim an exclusive early-bird discount when we arrive.
                        </p>

                        {waitlistSuccess ? (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                          >
                            <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                            <div>
                              <p className="text-sm font-semibold text-emerald-800">
                                You're on the list!
                              </p>
                              <p className="text-xs text-emerald-700 mt-0.5">
                                We'll notify you with an exclusive discount when Kavoa launches in {selectedCountryName}.
                              </p>
                            </div>
                          </motion.div>
                        ) : (
                          <form
                            onSubmit={async (e) => {
                              e.preventDefault();
                              setWaitlistSubmitting(true);
                              setWaitlistError("");
                              try {
                                await axios.post(`${GetBaseURL()}/api/auth/waitlist`, {
                                  email: waitlistForm.email,
                                  firmName: waitlistForm.firmName,
                                  country,
                                  message: waitlistForm.message,
                                });
                                setWaitlistSuccess(true);
                              } catch (err) {
                                setWaitlistError(apiErrorMessage(err, "Couldn't join the waitlist. Please try again."));
                              } finally {
                                setWaitlistSubmitting(false);
                              }
                            }}
                            className="space-y-3"
                          >
                            {/* Email */}
                            <div className="relative">
                              <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                              <input
                                type="email"
                                placeholder="Your email address"
                                className={inputClassName}
                                value={waitlistForm.email}
                                onChange={(e) =>
                                  setWaitlistForm((prev) => ({ ...prev, email: e.target.value }))
                                }
                                required
                              />
                            </div>

                            {/* Firm Name */}
                            <div className="relative">
                              <Building2 className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                              <input
                                type="text"
                                placeholder="Your firm name"
                                className={inputClassName}
                                value={waitlistForm.firmName}
                                onChange={(e) =>
                                  setWaitlistForm((prev) => ({ ...prev, firmName: e.target.value }))
                                }
                                required
                              />
                            </div>

                            {/* Message */}
                            <textarea
                              placeholder="Tell us about your practice..."
                              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-all focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none"
                              rows={2}
                              value={waitlistForm.message}
                              onChange={(e) =>
                                setWaitlistForm((prev) => ({ ...prev, message: e.target.value }))
                              }
                            />

                            {waitlistError && (
                              <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>{waitlistError}</span>
                              </div>
                            )}

                            {/* Submit */}
                            <button
                              type="submit"
                              disabled={waitlistSubmitting || !waitlistForm.email || !waitlistForm.firmName}
                              className="flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
                              style={{ backgroundColor: "#0F5132" }}
                            >
                              {waitlistSubmitting ? (
                                <>
                                  <Loader2 className="h-5 w-5 animate-spin" />
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <Send className="h-4 w-4" />
                                  Claim My Discount
                                </>
                              )}
                            </button>
                          </form>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Continue button for Canada / US */}
                <AnimatePresence>
                  {(country === "CA" || country === "US") && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <button
                        type="button"
                        onClick={() => setStep("details")}
                        style={{ backgroundColor: "#0F5132" }}
                        className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]"
                      >
                        Continue
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ) : (
              /* ── Step 2: Details Form ── */
              <motion.div
                key="details-step"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Back button */}
                <button
                  onClick={() => setStep("location")}
                  className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors mb-4"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>

                {/* Location badge */}
                <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full mb-4 w-fit">
                  <MapPin className="h-3.5 w-3.5" />
                  {selectedCountryName}
                </div>

                <h2 className="text-2xl font-bold text-slate-800 mb-1">
                  Create your account
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Get started in under a minute
                </p>

                {/* Error message */}
                {errorMessage && (
                  <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3.5">
                    <div className="flex items-start gap-2.5 text-sm font-medium text-red-700">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                    {errorType === "firm_exists" && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowAccessRequest(true);
                          setErrorMessage("");
                          setErrorType(null);
                        }}
                        className="mt-2.5 ml-6.5 text-sm font-semibold transition-colors hover:opacity-80"
                        style={{ color: "#0F5132" }}
                      >
                        Request Access Instead
                      </button>
                    )}
                    {errorType === "email_exists" && (
                      <p className="mt-2 ml-6.5 text-xs text-red-600">
                        Try signing in instead, or use a different email address.
                      </p>
                    )}
                    {errorType === "invited_user" && (
                      <p className="mt-2 ml-6.5 text-xs text-red-600">
                        Check your inbox for the invitation email and click the link to accept, or use the password sent to you to log in.
                      </p>
                    )}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Firm Name */}
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Law Firm Name
                    </span>
                    <div className="relative">
                      <Building2 className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        name="firmName"
                        placeholder="e.g. Smith & Associates"
                        className={inputClassName}
                        value={firmName}
                        onChange={(e) => setFirmName(e.target.value)}
                      />
                    </div>
                  </label>

                  {/* Name */}
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Your Name
                    </span>
                    <div className="relative">
                      <User className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        name="name"
                        placeholder="Jane Doe"
                        className={inputClassName}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>
                  </label>

                  {/* Email */}
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Email
                    </span>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        name="email"
                        placeholder="jane@lawfirm.com"
                        className={inputClassName}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </label>

                  {/* Password */}
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-semibold text-slate-700">
                      Password
                    </span>
                    <div className="relative">
                      <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="8+ characters"
                        className={`${inputClassName} pr-11`}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:text-slate-600"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <EyeOff className="h-[18px] w-[18px]" />
                        ) : (
                          <Eye className="h-[18px] w-[18px]" />
                        )}
                      </button>
                    </div>
                  </label>

                  {/* Consent Checkbox */}
                  <label className="flex items-start gap-3 mt-5 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={consentAccepted}
                      onChange={(e) => setConsentAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 accent-emerald-600"
                    />
                    <span className="text-xs text-slate-500 leading-relaxed">
                      I agree to the{" "}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-slate-700 transition-colors"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-slate-700 transition-colors"
                      >
                        Privacy Policy
                      </a>
                      , and consent to the collection and processing of my data as described.
                    </span>
                  </label>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting || !email || !password || !name || !firmName || !consentAccepted || !country}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white shadow-md transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ backgroundColor: "#0F5132" }}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Sign Up"
                    )}
                  </button>
                </form>

                {/* Divider */}
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium">or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                {/* Request Access section */}
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-3">
                    Your firm already uses Kavoa?
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAccessRequest(true);
                      setErrorMessage("");
                    }}
                    className="text-sm font-semibold transition-colors hover:opacity-80"
                    style={{ color: "#0F5132" }}
                  >
                    Request Access
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Modal>
  );
};

export default SignUpModal;
